import { useId, useState } from "react";
import { Button } from "@mui/material";
import { ImageCropDialog } from "./ImageCropDialog";

const DEFAULT_ACCEPT = [".jpg", ".jpeg", ".png", ".gif"];

const buildExtensionRegex = accept =>
  new RegExp(`(${accept.map(ext => `\\${ext}`).join("|")})$`, "i");

/**
 * Controlled image upload with preview + extension/size validation.
 * Does not know about Redux — callers wire `onError` to their own toast.
 *
 * `cropAspectRatio` (opcional, ej. `1` para círculo/cuadrado) abre un
 * diálogo de recorte con esa proporción fija antes de generar el preview
 * final — ver auditoría de imágenes de Tablados, 2026-09: sin esto, un
 * archivo que no viene ya cuadrado (ej. copiado de una foto de perfil de
 * Instagram) se sube tal cual y termina desencuadrado en el círculo donde
 * la app lo muestra. Sin esta prop, el comportamiento es exactamente el de
 * antes — otros callers (banners, que no son cuadrados) no se ven afectados.
 */
export const ImageUploadField = ({
  value,
  previewSrc,
  onChange,
  onError,
  accept = DEFAULT_ACCEPT,
  maxSizeMB = 5,
  label = "Cargar imagen",
  inputId,
  disabled = false,
  className = "",
  cropAspectRatio
}) => {
  const autoId = useId();
  const resolvedInputId = inputId || `upload-photo-${autoId}`;
  const [pendingFile, setPendingFile] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);

  const leerYNotificar = file => {
    const reader = new FileReader();
    reader.onload = e => {
      onChange?.(file, e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleChange = event => {
    const [file] = Array.from(event.target.files || []);
    if (!file) return;

    if (!buildExtensionRegex(accept).exec(file.name)) {
      onError?.("Tipo de archivo no válido");
      event.target.value = "";
      return;
    }

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      onError?.(`El archivo supera el tamaño máximo permitido (${maxSizeMB}MB)`);
      event.target.value = "";
      return;
    }

    if (cropAspectRatio) {
      setPendingFile(file);
      setCropOpen(true);
      return;
    }
    leerYNotificar(file);
  };

  const handleCropped = archivoRecortado => {
    setCropOpen(false);
    setPendingFile(null);
    leerYNotificar(archivoRecortado);
  };

  const handleCropCancel = () => {
    setCropOpen(false);
    setPendingFile(null);
  };

  return (
    <div className={`flex flex-col items-start gap-2 ${className}`}>
      <input
        style={{ display: "none" }}
        id={resolvedInputId}
        type="file"
        accept={accept.join(",")}
        onChange={handleChange}
        disabled={disabled}
      />
      {cropAspectRatio && (
        <ImageCropDialog
          open={cropOpen}
          file={pendingFile}
          aspectRatio={cropAspectRatio}
          onCancel={handleCropCancel}
          onCropped={handleCropped}
        />
      )}
      <label htmlFor={resolvedInputId}>
        <Button variant="outlined" component="span" disabled={disabled}>
          {label}
        </Button>
      </label>
      {value?.name && (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {value.name}
        </span>
      )}
      {previewSrc && (
        <div className="mt-2">
          <img
            src={previewSrc}
            alt="Vista previa"
            className="max-h-40 rounded-lg border border-gray-200 dark:border-gray-700"
          />
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
